import { useMemo } from 'react';
import { useListWorkspacesInfiniteQuery } from '@frontend/api-workspaces';
import { useWorkspaceShallowStore } from '../store';

export type WorkspaceValidationState = 'not-applicable' | 'validating' | 'valid' | 'invalid';

export interface WorkspaceValidation {
  state: WorkspaceValidationState;
  shouldAutoHeal: boolean;
}

/**
 * Validates whether the selectedWorkspaceId from localStorage still exists.
 *
 * **States:**
 * - `not-applicable`: No workspace selected, nothing to validate
 * - `validating`: Workspace list is loading, cannot confirm validity yet
 * - `valid`: Workspace exists in the loaded list
 * - `invalid`: Workspace list loaded but workspace not found OR query failed
 *
 * **Auto-heal logic:**
 * - Only auto-heal when state is 'invalid' (workspace confirmed missing or API failed)
 * - Do NOT auto-heal during 'validating' (optimistic: assume valid during loading)
 *
 * This hook is designed to be fail-safe:
 * - During loading → assume valid (don't block user)
 * - On error → mark invalid and auto-heal (exit workspace mode)
 * - On success but not found → mark invalid and auto-heal
 */
export function useValidateSelectedWorkspace(orgId: string): WorkspaceValidation {
  const { selectedWorkspaceId } = useWorkspaceShallowStore('selectedWorkspaceId');

  // Only fetch the workspace list when there is actually a workspace to validate.
  const workspacesQuery = useListWorkspacesInfiniteQuery(orgId, {
    enabled: !!selectedWorkspaceId,
  });

  return useMemo(() => {
    // No workspace selected - nothing to validate
    if (!selectedWorkspaceId) {
      return {
        state: 'not-applicable',
        shouldAutoHeal: false,
      };
    }

    // Workspace list is still loading - optimistically assume valid
    // This prevents blocking the UI while data loads
    if (workspacesQuery.isLoading || workspacesQuery.isFetching) {
      return {
        state: 'validating',
        shouldAutoHeal: false,
      };
    }

    // Query failed - mark as invalid and trigger auto-heal
    // Fail-safe: if we can't load workspaces, exit workspace mode
    if (workspacesQuery.isError) {
      return {
        state: 'invalid',
        shouldAutoHeal: true,
      };
    }

    // Query succeeded - check if workspace exists in the list
    const allWorkspaces = workspacesQuery.data?.pages.flatMap((p) => p.workspaces ?? []) ?? [];
    const workspaceExists = allWorkspaces.some((w) => w.workspaceId === selectedWorkspaceId);

    if (workspaceExists) {
      return {
        state: 'valid',
        shouldAutoHeal: false,
      };
    }

    // Workspace not found in list - mark as invalid and trigger auto-heal
    return {
      state: 'invalid',
      shouldAutoHeal: true,
    };
  }, [
    selectedWorkspaceId,
    workspacesQuery.isLoading,
    workspacesQuery.isFetching,
    workspacesQuery.isError,
    workspacesQuery.data,
  ]);
}
